import { useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useWatch } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { Alert, Grid } from '@mui/material'
import { useSnackbar } from 'notistack'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import { UpdatePatientDto, PatientIdentityStatus } from '@/core/models/patient.model'
import { ConfidenceLevel } from '@/core/models/identity-justification.model'
import { useVitaleCardDisplayLogic } from '@/widgets/vitale-card'
import { SavedAddress } from '@/core/types/shared'

import { useIsSA } from '@/hooks/useIsSA'

import { PatientProfileFormData, usePatientProfileForm, UsePatientProfileFormProps } from '../model/usePatientProfileForm'
import { useVerifyInsBeforeUpdate } from '../model/useVerifyInsBeforeUpdate'
import { useVerifyInsOnCreate } from '../model/useVerifyInsOnCreate'
import { InsInfoSection } from './InsInfoSection'
import { GeneralInfoSection } from './GeneralInfoSection'
import { BillingInfoSection } from './BillingInfoSection'
import { MedicalInfoSection } from './MedicalInfoSection'
import { AddressInfoSection } from './AddressInfoSection'
import { SavedAddressesSection } from './SavedAddressesSection'
import { ConsentSection } from './ConsentSection'
import { PrivateDoctorSection } from './PrivateDoctorSection'
import { ParentsSection } from './ParentsSection'
import { PregnancySection } from '@/features/pregnancy'
import { useCreatePatient } from '../model/useCreatePatient'
import { useUpdatePatient } from '../model/useUpdatePatient'
import { Dispatch, SetStateAction } from 'react'
import { useGetAdminEstablishmentQuery } from '@/core/services/admin'

type CommonProps = {
  action?: ({ isSubmitting }: { isSubmitting: boolean }) => React.ReactNode
  actionPlacement?: 'top' | 'bottom'
  editable?: boolean
  identityStatus?: PatientIdentityStatus | null
  intro?: boolean
  isReferenceIdentityLocked?: boolean
  onSubmit?: SubmitHandler<PatientProfileFormData>
  savedAddresses?: SavedAddress[]
}

export type PatientProfileFormProps =
  | (UsePatientProfileFormProps & CommonProps & { mode: 'create'; onUpdate?: never; patientId?: never; setEditable?: never })
  | (UsePatientProfileFormProps &
      CommonProps & { mode: 'edit'; onUpdate?: (dto: UpdatePatientDto) => void; patientId: string; setEditable: Dispatch<SetStateAction<boolean>> })

export function PatientProfileForm(props: PatientProfileFormProps) {
  const {
    action,
    actionPlacement = 'bottom',
    editable: editableProp = false,
    errors,
    identityStatus,
    initialValues,
    intro = false,
    isReferenceIdentityLocked = false,
    mode,
    onSubmit: onSubmitProp,
    savedAddresses,
    setEditable,
  } = props
  const isSA = useIsSA()
  const { enqueueSnackbar } = useSnackbar()
  const { isINSEnabled = false, rhForm, ssnLength } = usePatientProfileForm({ initialValues, errors })
  const { data: establishment } = useGetAdminEstablishmentQuery()
  const identityJustificationTypes = establishment?.identityJustificationTypes || []
  const autoValidateIdentity = establishment?.autoValidateIdentity
  const [createPatient, isCreating] = useCreatePatient(intro)
  const [updatePatient, isUpdating] = useUpdatePatient({ onUpdate: props.onUpdate })
  const verifyInsBeforeUpdate = useVerifyInsBeforeUpdate({ isINSEnabled, initialValues, patientId: props.patientId })
  const verifyInsOnCreate = useVerifyInsOnCreate({ isINSEnabled })
  const editable = editableProp || mode === 'create'
  const isSubmitting = isCreating || isUpdating

  const onSubmit: SubmitHandler<PatientProfileFormData> = async (data) => {
    if (onSubmitProp) {
      onSubmitProp(data)
      return
    }

    switch (mode) {
      case 'create':
        {
          const safeData = await verifyInsOnCreate(data)
          await createPatient(safeData)
        }
        return
      case 'edit':
        {
          const canProceed = await verifyInsBeforeUpdate(data)
          if (!canProceed) {
            setEditable(false)
            enqueueSnackbar(t`patient information not updated`, { variant: 'info' })
            return
          }
        }

        await updatePatient(data, props.patientId)
        setEditable(false)
        return
      default:
        return
    }
  }

  const identityJustificationType = useWatch({ control: rhForm.control, name: 'identityJustificationType' })

  const canValidateIdentity = useMemo(() => {
    if (!identityJustificationType) return false
    const selectedType = identityJustificationTypes.find((type) => type._id === identityJustificationType)
    const level = selectedType?.confidenceLevel
    return level?.toLowerCase() === ConfidenceLevel.HIGH.toLowerCase()
  }, [identityJustificationType, identityJustificationTypes])

  const { showDMPConnectVitaleCard } = useVitaleCardDisplayLogic()
  const showIdentityJustificationField = isINSEnabled && (showDMPConnectVitaleCard || identityJustificationTypes.length > 0)
  const identityJustificationOptions = useMemo(() => {
    const placeholder = { label: t`Select identity justification type`, value: '' }
    const typesWithId = identityJustificationTypes.filter((type) => type._id != null && type._id !== '')
    return [
      placeholder,
      ...typesWithId.map((type) => {
        const confidenceLevelLabel = type.confidenceLevel?.toLowerCase() === ConfidenceLevel.HIGH.toLowerCase() ? t`High` : t`Low`
        return {
          label: `${type.label} (${confidenceLevelLabel})`,
          value: type._id,
        }
      }),
    ]
  }, [identityJustificationTypes])

  useEffect(() => {
    if (!establishment) return
    if ((!canValidateIdentity && identityJustificationType) || identityJustificationType === '') {
      rhForm.setValue('identityConfirmed', false)
    }
  }, [canValidateIdentity, identityJustificationType, rhForm, establishment])

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(onSubmit)} id="patientForm">
        {actionPlacement === 'top' && action?.({ isSubmitting })}
        <Grid container rowSpacing={3} columnSpacing={10}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isINSEnabled ? (
                <Grid item xs={12}>
                  <InsInfoSection editable={editable} identityStatus={identityStatus} isReferenceIdentityLocked={isReferenceIdentityLocked} mode={mode} />
                </Grid>
              ) : (
                <Grid id="patientInformation" item xs={12}>
                  <GeneralInfoSection editable={editable} isINSEnabled={isINSEnabled} mode={mode} ssnLength={ssnLength} />
                </Grid>
              )}
              {!isSA && (
                <Grid item xs={12}>
                  <BillingInfoSection editable={editable} />
                </Grid>
              )}
              <Grid item xs={12}>
                <MedicalInfoSection editable={editable} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isINSEnabled && (
                <Grid id="patientInformation" item xs={12}>
                  <GeneralInfoSection editable={editable} isINSEnabled={isINSEnabled} mode={mode} ssnLength={ssnLength} />
                </Grid>
              )}
              <Grid id="adress-information" item xs={12}>
                <AddressInfoSection editable={editable} />
              </Grid>
              {mode === 'edit' && savedAddresses?.length ? (
                <Grid id="saved-addresses" item xs={12}>
                  <SavedAddressesSection savedAddresses={savedAddresses} />
                </Grid>
              ) : null}
              {mode === 'create' && !isSA && (
                <Grid item id="agreement" xs={12}>
                  <ConsentSection />
                </Grid>
              )}
              {!isSA && (
                <Grid id="extra-info" item xs={12}>
                  <PrivateDoctorSection editable={editable} mode={mode} />
                </Grid>
              )}
              <Grid item xs={12}>
                <ParentsSection readOnly={!editable} />
              </Grid>
              {mode === 'edit' && props.patientId && (
                <Grid item xs={12}>
                  <PregnancySection patientId={props.patientId} readOnly={!editable} />
                </Grid>
              )}
              <Grid item xs={12}>
                {showIdentityJustificationField && (
                  <SelectInputController
                    control={rhForm.control}
                    fullWidth
                    label={t`I qualify the identity of my patient`}
                    name="identityJustificationType"
                    options={identityJustificationOptions}
                    readOnly={!editable}
                    sx={{ mb: 2 }}
                    InputLabelProps={{
                      shrink: true,
                      sx: {
                        fontSize: '1rem',
                        fontWeight: 500,
                        transform: 'none',
                        position: 'relative',
                        color: 'text.primary',
                      },
                    }}
                  />
                )}
                {canValidateIdentity && autoValidateIdentity && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Trans>Identity with a high-confidence justification type will be marked as validated automatically</Trans>
                  </Alert>
                )}
                {showIdentityJustificationField && !canValidateIdentity && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Trans>Identity can only be marked as validated with a high-confidence justification type</Trans>
                  </Alert>
                )}
                <CheckboxInputController
                  control={rhForm.control}
                  label={t`I confirm that the patient's identity and information are correct`}
                  name="identityConfirmed"
                  disabled={showIdentityJustificationField && !canValidateIdentity}
                  readOnly={!editable}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {actionPlacement === 'bottom' && action?.({ isSubmitting })}
      </form>
    </FormProvider>
  )
}
